
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePetCategoryCounts } from '../../hooks/usePublicPets';
import { Skeleton } from '../ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Briefcase, ArrowRight } from 'lucide-react';

// Category images
import allPetsImg from '@/assets/categories/all-pets.jpg';
import dogsImg from '@/assets/categories/dogs.jpg';
import catsImg from '@/assets/categories/cats.jpg';
import birdsImg from '@/assets/categories/birds.jpg';
import fishImg from '@/assets/categories/fish.jpg';
import reptilesImg from '@/assets/categories/reptiles.jpg';
import smallPetsImg from '@/assets/categories/small-pets.jpg';
import horsesImg from '@/assets/categories/horses.jpg';
import cattleImg from '@/assets/categories/cattle.jpg';
import otherImg from '@/assets/categories/other.jpg';

interface CategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CategoriesModal = ({ isOpen, onClose }: CategoriesModalProps) => {
  const { data: categoryCounts, isLoading } = usePetCategoryCounts();
  const { t } = useTranslation();

  const categoryConfig = [
    { id: 'all', name: t('categories.allPets', 'All Pets'), image: allPetsImg },
    { id: 'dog', name: t('categories.dogs', 'Dogs'), image: dogsImg },
    { id: 'cat', name: t('categories.cats', 'Cats'), image: catsImg },
    { id: 'bird', name: t('categories.birds', 'Birds'), image: birdsImg },
    { id: 'fish', name: t('categories.fish', 'Fish'), image: fishImg },
    { id: 'reptile', name: t('categories.reptiles', 'Reptiles'), image: reptilesImg },
    { id: 'small_pet', name: t('categories.smallPets', 'Small Pets'), image: smallPetsImg },
    { id: 'horse', name: t('categories.horses', 'Horses'), image: horsesImg },
    { id: 'cattle', name: t('categories.cattle', 'Cattle'), image: cattleImg },
    { id: 'other', name: t('categories.other', 'Other'), image: otherImg },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-bold">{t('feed.browseCategories')}</DialogTitle>
        </DialogHeader>
        
        <div className="py-2 space-y-4">
          {/* Pet Categories Section */}
          <div>
            <h3 className="text-sm font-bold text-green-600 mb-3 px-1">{t('categories.petCategories')}</h3>
            {isLoading ? (
              <div className="grid grid-cols-5 gap-2">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-square rounded-xl" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-2">
                {categoryConfig.map((category) => {
                  const count = categoryCounts?.[category.id] || 0;
                  
                  return (
                    <Link
                      key={category.id}
                      to={`/pets/category/${category.id}`}
                      onClick={onClose}
                      className="group flex flex-col items-center text-center"
                    >
                      <div className="relative w-full aspect-square rounded-xl overflow-hidden mb-1.5 shadow-sm group-hover:shadow-md transition-shadow ring-1 ring-black/5">
                        <img
                          src={category.image}
                          alt={category.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          loading="lazy"
                        />
                        {/* Gradient overlay for text readability */}
                        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/50 to-transparent" />
                      </div>
                      <span className="text-xs font-semibold text-foreground/90 leading-tight">
                        {category.name}
                      </span>
                      {count > 0 && (
                        <span className="text-[10px] text-muted-foreground leading-tight">
                          {count} pets
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          <div className="h-px bg-border" />

          {/* Services Section */}
          <div>
            <h3 className="text-sm font-bold text-blue-600 mb-2 px-1">{t('services.petServices')}</h3>
            
            {/* Main "Browse All Services" button */}
            <Link
              to="/services"
              onClick={onClose}
              className="group flex items-center justify-between bg-gradient-to-r from-pet-purple to-pet-blue rounded-xl p-4 hover:shadow-lg transition-all text-white"
            >
              <div className="flex items-center gap-3">
                <Briefcase className="h-6 w-6" />
                <div>
                  <div className="font-semibold text-base">
                    {t('services.title')}
                  </div>
                  <div className="text-sm text-white/80">
                    {t('services.subtitle')}
                  </div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CategoriesModal;
